import type { MessageEntrySource } from "./react-message-view";

/**
 * Subscribable per-ID message store. Absorbs useQuery deep-clone churn —
 * diffs per-entry and only notifies subscribers whose data actually changed.
 *
 * Used as reconnect fallback when ReactMessageView is not active.
 * Implements MessageEntrySource for compatibility with useMessageData.
 *
 * Used by MessageRow via useSyncExternalStore to achieve per-message
 * re-render granularity.
 */

/** Typed message fields consumed by the Message component. */
export interface MessageData {
  id: string;
  user_id: string;
  content: string;
  created_at: number;
  updated_at?: number;
  /** Typed-kind discriminator (Phase 2+3). Readers dispatch on this. */
  kind?: string | null;
  /** Kind-specific render-only payload. */
  payload?: Record<string, unknown> | null;
  /** Pre-rendered preview (derived by the builder at insert time). */
  preview_text?: string | null;
  is_edited?: boolean;
  is_broadcast?: boolean | null;
  metadata?: {
    agent?: boolean;
    coworker?: boolean;
    model?: string;
    generated_at?: number;
    error?: boolean;
    /** Persisted reasoning text for the "Thought process" chip. */
    thinking?: string;
  } | null;
  mentions?: string[] | null;
  thread_id?: string | null;
  reply_count?: number | null;
  reply_users?: string[] | null;
  latest_reply_at?: number | null;
  /** Inline-reply citation. Set at insert and never edited. */
  replied_to_message_id?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Allow extra fields from Zero query results
}

/** Compare string arrays by value (order-sensitive). */
function stringArrayEqual(
  a: string[] | null | undefined,
  b: string[] | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Shallow compare of metadata object. If metadata schema gains new
 * fields, add them here — otherwise edits to those fields won't
 * trigger re-renders.
 */
function metadataEqual(
  a: MessageData["metadata"],
  b: MessageData["metadata"],
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  return (
    a.agent === b.agent &&
    a.coworker === b.coworker &&
    a.model === b.model &&
    a.generated_at === b.generated_at &&
    a.error === b.error &&
    a.thinking === b.thinking
  );
}

// Child data (reactions, attachments, link_previews) no longer arrives
// via the message query — it comes from separate child table selectors.
// Only message-level fields need diffing here.
function entryEqual(prev: MessageData, next: MessageData): boolean {
  return (
    prev.content === next.content &&
    prev.is_edited === next.is_edited &&
    prev.is_broadcast === next.is_broadcast &&
    prev.reply_count === next.reply_count &&
    prev.latest_reply_at === next.latest_reply_at &&
    prev.user_id === next.user_id &&
    prev.created_at === next.created_at &&
    prev.updated_at === next.updated_at &&
    prev.thread_id === next.thread_id &&
    // `replied_to_message_id` is set at insert and never edited, so
    // diffing it is correctness-only.
    prev.replied_to_message_id === next.replied_to_message_id &&
    stringArrayEqual(prev.reply_users, next.reply_users) &&
    metadataEqual(prev.metadata, next.metadata) &&
    stringArrayEqual(prev.mentions, next.mentions)
  );
}

export class MessageDataStore implements MessageEntrySource {
  #entries = new Map<string, MessageData>();
  #listeners = new Map<string, Set<() => void>>();
  #pendingNotifications = new Set<string>();
  #version = 0;

  /**
   * Phase 1 (call during render): diff per-entry, update the Map,
   * queue changed IDs. Does NOT notify subscribers — safe to call
   * while React is rendering another component.
   */
  update(messages: readonly MessageData[]): void {
    const nextIds = new Set<string>();

    for (const msg of messages) {
      nextIds.add(msg.id);
      const prev = this.#entries.get(msg.id);
      if (!prev || !entryEqual(prev, msg)) {
        this.#entries.set(msg.id, msg);
        this.#pendingNotifications.add(msg.id);
      }
    }

    // Remove entries no longer in the list
    for (const id of this.#entries.keys()) {
      if (!nextIds.has(id)) {
        this.#entries.delete(id);
        this.#pendingNotifications.add(id);
      }
    }

    if (this.#pendingNotifications.size > 0) this.#version++;
  }

  /**
   * Phase 2 (call from useEffect): fire queued notifications.
   * Subscribers (useSyncExternalStore in MessageRow) schedule
   * re-renders for rows whose data actually changed.
   */
  flush(): void {
    const pending = this.#pendingNotifications;
    if (pending.size === 0) return;
    this.#pendingNotifications = new Set<string>();
    for (const id of pending) {
      const listeners = this.#listeners.get(id);
      if (listeners) {
        for (const cb of listeners) cb();
      }
    }
  }

  get(id: string): MessageData | undefined {
    return this.#entries.get(id);
  }

  get version(): number {
    return this.#version;
  }

  subscribe(id: string, cb: () => void): () => void {
    let listeners = this.#listeners.get(id);
    if (!listeners) {
      listeners = new Set();
      this.#listeners.set(id, listeners);
    }
    listeners.add(cb);
    return () => {
      listeners!.delete(cb);
      if (listeners!.size === 0) this.#listeners.delete(id);
    };
  }
}
